import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { FormLabel, Grid } from '@material-ui/core';

import { GlobalContext } from '../../store';
import { INotificationMessage } from '../../store/admin/clubs/actions';
import { IAdminClub } from '../../store/admin/clubs/store';
import { IAdminEvent } from '../../store/admin/events/store';

import speaker from '../../assets/images/speaker.svg';
import AutoComplete from '../AutoComplete';
import Button from '../Button';
import Input from '../Input';
import RadioGroup from '../RadioGroup';
import { IDisableableOption } from '../RadioGroup/RadioGroup';
import { IOption } from '../Select/Select';
import { SendNotificationFormStyled } from './SendNotificationForm.styles';

interface IProps {
  submitCallback?: () => void;
  club: IAdminClub;
  eventId?: string;
}

const SendNotificationForm: FC<IProps> = props => {
  const { submitCallback, club, eventId } = props;

  const {
    actions: {
      adminClubs: { sendNotification }
    },
    adminEvents: events
  } = useContext(GlobalContext);

  const [inputValue, setInputValue] = useState('');
  const [selectValue, setSelectValue] = useState('entire-club');
  const [selectedEventFieldId, setSelectedEventFieldId] = useState(0);
  const [radioGroupOptions, setRadioGroupOptions] = useState<IDisableableOption[]>([]);
  const [eventFieldAutocompleteOptions, setEventFieldAutocompleteOptions] = useState<IOption[]>([]);

  useEffect(() => {
    if (events && Object.keys(events).length > 0) {
      const eventValues: IAdminEvent[] = Object.values(events);

      setSelectedEventFieldId(eventValues[0].id);
    }
  }, [events]);

  const submitNotification = useCallback(async () => {
    if (sendNotification) {
      const notification: INotificationMessage =
        selectValue === 'entire-club'
          ? { club: club.id, message: inputValue }
          : { event: eventId ? Number(eventId) : selectedEventFieldId, message: inputValue };

      await sendNotification(notification);

      if (submitCallback) {
        submitCallback();
      }
    }
  }, [inputValue, selectedEventFieldId, selectValue]);

  const onInputChange = useCallback((event: any) => setInputValue(event.target.value), [
    setInputValue
  ]);

  const onRadioGroupChange = useCallback((event: any) => setSelectValue(event.target.value), [
    setSelectValue
  ]);

  const eventFieldAutocompleteOnSelect = useCallback(
    (item: any) => setSelectedEventFieldId(item.value),
    [setSelectedEventFieldId]
  );

  useEffect(() => {
    const options = [{ label: 'Entire Club', value: 'entire-club' }];

    const extraOption = eventId
      ? {
          label: 'Event players only',
          value: 'event-field'
        }
      : {
          disabled: !events || Object.keys(events.all).length === 0,
          label: 'Only to an event field',
          value: 'event-field'
        };

    setRadioGroupOptions([...options, extraOption]);
  }, [events]);

  useEffect(() => {
    setEventFieldAutocompleteOptions(
      Object.values((events || { all: {} }).all).map(event => ({
        label: event.name,
        value: event.id.toString()
      }))
    );
  }, [events]);

  return (
    <SendNotificationFormStyled id="alert-dialog-content">
      <Input
        fullWidth={true}
        id="notification-body"
        label="Leave your message..."
        rows={4}
        multiline={true}
        noIcon={true}
        onChange={onInputChange}
        value={inputValue}
        outlined={true}
        showCharAmount={true}
        type="text"
      />

      <Grid className="grid radio-group-container" container={true} spacing={2}>
        <Grid className="grid-item main-label" item={true} xs={2}>
          <FormLabel className="send-form-label">Send to</FormLabel>
        </Grid>

        <Grid className="grid-item right" item={true} xs={10}>
          <RadioGroup
            value={selectValue}
            options={radioGroupOptions}
            onChange={onRadioGroupChange}
          />

          <div className={`event-selector ${selectValue === 'event-field' ? 'hidden' : ''}`} />

          {selectValue === 'event-field' && !eventId && events && (
            <AutoComplete
              outlined={true}
              title={'EVENT'}
              onSelect={eventFieldAutocompleteOnSelect}
              options={eventFieldAutocompleteOptions}
            />
          )}
        </Grid>

        <Grid className="grid-item modal-action" item={true} xs={12}>
          <Button
            className="sendButton"
            classes={{ disabled: 'sendButton-disabled' }}
            variant="contained"
            color="primary"
            type="button"
            onClick={submitNotification}
            disabled={inputValue.length === 0}
          >
            <div className="icon">
              <img src={speaker} alt="" />
            </div>
            Send
          </Button>
        </Grid>
      </Grid>
    </SendNotificationFormStyled>
  );
};

export default SendNotificationForm;
